/* eslint-disable security/detect-object-injection */
import { ICoreContext } from '@msdyn365-commerce/core';
import { Table } from '@tanstack/react-table';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { IOrderHistoryTableData } from '../../../actions/order-history/get-order-history.action';
import postExcelConfigAction, { ISaveExcelConfigData, SaveExcelConfigInput } from '../../../actions/order-history/post-excel-config.action';
import Label from '../../../components/label';
import { XLSX_TABLE_GROUP_KEY_MAPPING, XLSX_TABLE_KEY_MAPPING } from '../../../configuration';
import { ILocalizationState } from '../../../states/localization-state';
import { exportToExcel } from '../../../utilities/export-to-xlsx';
import { runActionAndHandleAuth } from '../../../utilities/is-authenticated';
import { mapXlsxGroupHeader } from '../../../utilities/map-xlsx-group-header';
import { IOrderHistoryResources } from '../order-history.props.autogenerated';
import { ConfigurationItem } from './configuration-item';
import { ModalFooter } from './modal-footer';

interface ExportConfigurationModalProps {
    CLASS_NAME: string;
    table: Table<IOrderHistoryTableData>;
    localizationState: ILocalizationState;
    resources: IOrderHistoryResources;
    context: ICoreContext;
    excelConfig: string[];
    onConfigUpdate: (newConfig: string[]) => void;
    onClose: () => void;
}

interface GroupDef {
    id: string;
    title: string;
    keys: string[];
}

interface GroupHeaderProps {
    group: GroupDef;
    selected: Set<string>;
    className: string;
    isCollapsed: boolean;
    resources: IOrderHistoryResources;
    onSelectGroup: (keys: string[]) => void;
    onClearGroup: (keys: string[]) => void;
    onToggleCollapse: (id: string) => void;
}

const buildGroups = (resources: IOrderHistoryResources): GroupDef[] => {
    return Object.entries(XLSX_TABLE_GROUP_KEY_MAPPING).map(([groupId, keys]) => ({
        id: groupId,
        title: (resources as any)[groupId] ?? groupId,
        keys: keys
    }));
};

const GroupHeader = ({
    group,
    selected,
    className,
    isCollapsed,
    resources,
    onSelectGroup,
    onClearGroup,
    onToggleCollapse
}: GroupHeaderProps) => {
    const checkboxRef = useRef<HTMLInputElement | null>(null);
    const keys = group.keys ?? [];

    const allSelected = keys.length > 0 && keys.every(k => selected.has(k));
    const someSelected = keys.some(k => selected.has(k));
    const isIndeterminate = someSelected && !allSelected;

    useEffect(() => {
        if (checkboxRef.current) {
            checkboxRef.current.indeterminate = isIndeterminate;
        }
    }, [isIndeterminate]);

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            onSelectGroup(group.keys);
        } else {
            onClearGroup(group.keys);
        }
    };

    const handleHeaderClick = () => onToggleCollapse(group.id);

    return (
        <div className={`${className}__group-header`} onClick={handleHeaderClick}>
            <div className={`${className}__group-header-content`}>
                <input
                    ref={checkboxRef}
                    type='checkbox'
                    checked={allSelected}
                    aria-checked={isIndeterminate ? 'mixed' : allSelected}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    onChange={handleCheckboxChange}
                />
                <h4 className={`${className}__group-title`}>{mapXlsxGroupHeader(group.title, resources)}</h4>
            </div>
            <div className={`${className}__group-header-expand ${isCollapsed ? 'collapsed' : 'expanded'}`} />
        </div>
    );
};

const ExportConfigurationModal: React.FC<ExportConfigurationModalProps> = ({
    CLASS_NAME,
    table,
    localizationState,
    resources,
    context,
    excelConfig,
    onClose,
    onConfigUpdate
}) => {
    const mappingEntries = useMemo(() => Object.entries(XLSX_TABLE_KEY_MAPPING), []);
    const groups = useMemo(() => buildGroups(resources), [resources]);

    const [selected, setSelected] = useState<Set<string>>(new Set(excelConfig));
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);

    const toggleField = (key: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const selectGroup = (keys: string[]) => {
        setSelected(prev => {
            const next = new Set(prev);
            keys.forEach(key => next.add(key));
            return next;
        });
    };

    const clearGroup = (keys: string[]) => {
        setSelected(prev => {
            const next = new Set(prev);
            keys.forEach(key => next.delete(key));
            return next;
        });
    };

    const toggleGroupCollapse = (groupId: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const toggleSelectAll = (isChecked: boolean) => {
        if (isChecked) {
            setSelected(new Set(mappingEntries.map(([key]) => key)));
        } else {
            setSelected(new Set());
        }
    };

    const toggleExpandAll = (isChecked: boolean) => {
        setCollapsedGroups(isChecked ? new Set() : new Set(groups.map(group => group.id)));
    };

    const handleExportClick = async () => {
        setIsSaving(true);
        try {
            const selectedArray = Array.from(selected);

            const postExcelConfigData: ISaveExcelConfigData = { Settings: selectedArray };
            const actionInput = new SaveExcelConfigInput(postExcelConfigData);
            await runActionAndHandleAuth(() => postExcelConfigAction(actionInput, context.actionContext), context.request.user);
            await exportToExcel(table.getSortedRowModel().rows, localizationState, resources, selectedArray);
            onConfigUpdate(selectedArray);
        } catch (e) {
            console.error('Export failed', e);

            toast.error(
                <div className='toast' role='alert'>
                    <div className='toast-header'>
                        <h4>{resources.orderHistoryExportConfigFailedHeading}</h4>
                        <button className='toast-close' onClick={() => toast.dismiss()}></button>
                    </div>
                    <div className='toast-body'>
                        <span>{resources.orderHistoryExportConfigFailedDescription}</span>
                    </div>
                </div>
            );
        } finally {
            setIsSaving(false);
            onClose();
        }
    };

    if (isSaving) {
        return <Label className='ms-order-history__loading' />;
    }

    const allFieldsSelected = selected.size === mappingEntries.length;
    const allGroupsExpanded = collapsedGroups.size === 0;

    return (
        <div className={CLASS_NAME}>
            <div className={`${CLASS_NAME}__group`}>
                <div className={`${CLASS_NAME}__group-configure-all`}>
                    <label className={`${CLASS_NAME}__checkbox-label`}>
                        <input type='checkbox' checked={allFieldsSelected} onChange={e => toggleSelectAll(e.target.checked)} />
                        {resources.orderHistoryExcelConfigurationCheckAllPlaceholder}
                    </label>
                    <label className={`${CLASS_NAME}__checkbox-label`}>
                        <input type='checkbox' checked={allGroupsExpanded} onChange={e => toggleExpandAll(e.target.checked)} />
                        {resources.orderHistoryExcelConfigurationExpandAllPlaceholder}
                    </label>
                </div>
            </div>

            <div className={`${CLASS_NAME}__groups`}>
                {groups.map(group => {
                    const isCollapsed = collapsedGroups.has(group.id);
                    return (
                        <section key={group.id} className={`${CLASS_NAME}__group ${isCollapsed ? `${CLASS_NAME}__group--collapsed` : ''}`}>
                            <GroupHeader
                                group={group}
                                selected={selected}
                                onSelectGroup={selectGroup}
                                onClearGroup={clearGroup}
                                onToggleCollapse={toggleGroupCollapse}
                                className={CLASS_NAME}
                                isCollapsed={isCollapsed}
                                resources={resources}
                            />

                            {!isCollapsed && (
                                <div className={`${CLASS_NAME}__fields-list`}>
                                    {group.keys.map(key => {
                                        const translationKey = XLSX_TABLE_KEY_MAPPING[key];
                                        if (!translationKey) {
                                            return null;
                                        }

                                        const labelText = (resources as any)[translationKey] ?? translationKey;
                                        const isChecked = selected.has(key);

                                        return (
                                            <ConfigurationItem
                                                key={key}
                                                className={CLASS_NAME}
                                                label={labelText}
                                                isChecked={isChecked}
                                                onToggle={() => toggleField(key)}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            <ModalFooter className={CLASS_NAME} resources={resources} onClose={onClose} onExport={handleExportClick} />
        </div>
    );
};

export default ExportConfigurationModal;
